import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    ScheduleOfActivitiesSection,
    StudyEntityType,
    StudySection,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import {
    useScheduleOfActivitiesSectionDelete,
    useScheduleOfActivitiesSectionLink,
    useScheduleOfActivitiesSectionMove,
    useScheduleOfActivitiesSectionRename,
    useScheduleOfActivitiesSectionUpdate,
} from './ScheduleOfActivitiesSectionHooks';

export type ScheduleOfActivitiesSectionInsertHook = SectionInsertHook<Partial<ScheduleOfActivitiesSection>>;

/**
 * Mutation hook to insert a schedule of activities section.
 * @returns A function that inserts a schedule of activities section.
 */
export const useScheduleOfActivitiesSectionInsertMutation: (studyId: string) => ScheduleOfActivitiesSectionInsertHook =
    createSectionInsertMutation<Partial<ScheduleOfActivitiesSection>>(
        StudyEntityType.ScheduleOfActivitiesSection,
        'schedule-of-activities-section',
        'Failed to create schedule of activities section'
    );

export type ScheduleOfActivitiesSectionUpdateHook = (
    scheduleOfActivitiesSection: ScheduleOfActivitiesSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update a schedule of activities section.
 * @returns A function that updates a schedule of activities section.
 */
export function useScheduleOfActivitiesSectionUpdateMutation(studyId: string): ScheduleOfActivitiesSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateScheduleOfActivitiesSection = useScheduleOfActivitiesSectionUpdate({ studyId });

    function update(scheduleOfActivitiesSection: ScheduleOfActivitiesSection) {
        return updateScheduleOfActivitiesSection
            .mutateAsync(scheduleOfActivitiesSection)
            .catch(showErrorHandler('Failed to update schedule of activities section'));
    }

    return update;
}

export type ScheduleOfActivitiesSectionDeleteHook = (scheduleOfActivitiesSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete a schedule of activities section.
 * @returns A function that deletes a schedule of activities section.
 */
export function useScheduleOfActivitiesSectionDeleteMutation(studyId: string): ScheduleOfActivitiesSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteScheduleOfActivitiesSection = useScheduleOfActivitiesSectionDelete({ studyId });

    function deleteSection(scheduleOfActivitiesSection: { id: string }) {
        return deleteScheduleOfActivitiesSection
            .mutateAsync({
                id: scheduleOfActivitiesSection.id,
                studyId,
            })
            .catch(showErrorHandler('Failed to delete schedule of activities section'));
    }

    return deleteSection;
}

export type ScheduleOfActivitiesSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate a schedule of activities section.
 * @returns A function that duplicates a schedule of activities section.
 */
export const useScheduleOfActivitiesSectionDuplicateMutation: (
    studyId: string
) => ScheduleOfActivitiesSectionDuplicateHook = createSectionDuplicateMutation(
    StudyEntityType.ScheduleOfActivitiesSection,
    'schedule-of-activities-section',
    'Failed to duplicate schedule of activities section'
);

export type ScheduleOfActivitiesSectionMoveHook = (scheduleOfActivitiesSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move a schedule of activities section.
 * @returns A function that moves a schedule of activities section.
 */
export function useScheduleOfActivitiesSectionMoveMutation(studyId: string): ScheduleOfActivitiesSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveScheduleOfActivitiesSection = useScheduleOfActivitiesSectionMove({ studyId });

    function move(scheduleOfActivitiesSection: { id: string; placement: PlacementPayload }) {
        return moveScheduleOfActivitiesSection
            .mutateAsync({
                ...scheduleOfActivitiesSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to move schedule of activities section'));
    }

    return move;
}

export type ScheduleOfActivitiesSectionLinkHook = (scheduleOfActivitiesSection: {
    id: string;
    linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to link a schedule of activities section.
 * @returns A function that links a schedule of activities section.
 */
export function useScheduleOfActivitiesSectionLinkMutation(studyId: string): ScheduleOfActivitiesSectionLinkHook {
    const { showErrorHandler } = useAppContext();

    const linkScheduleOfActivitiesSection = useScheduleOfActivitiesSectionLink({ studyId });

    function link(scheduleOfActivitiesSection: {
        id: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }) {
        return linkScheduleOfActivitiesSection
            .mutateAsync({
                ...scheduleOfActivitiesSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to link schedule of activities section'));
    }

    return link;
}

export type ScheduleOfActivitiesSectionRenameHook = (scheduleOfActivitiesSection: {
    id: string;
    renamePayload: RenamePayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to rename a schedule of activities section.
 * @returns A function that renames a schedule of activities section.
 */
export function useScheduleOfActivitiesSectionRenameMutation(studyId: string): ScheduleOfActivitiesSectionRenameHook {
    const { showErrorHandler } = useAppContext();

    const renameScheduleOfActivitiesSection = useScheduleOfActivitiesSectionRename({ studyId });

    function rename(scheduleOfActivitiesSection: { id: string; renamePayload: RenamePayload }) {
        return renameScheduleOfActivitiesSection
            .mutateAsync({
                ...scheduleOfActivitiesSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to rename schedule of activities section'));
    }

    return rename;
}
